import { AlertTriangle } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  isDark: boolean;
}

export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  isDark,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const theme = {
    overlay: isDark ? 'bg-black/80' : 'bg-black/50',
    bg: isDark ? 'bg-[#1a1f2e]' : 'bg-white',
    border: isDark ? 'border-white/10' : 'border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textMuted: isDark ? 'text-white/70' : 'text-gray-600',
    btnPrimary: confirmVariant === 'danger'
      ? 'bg-red-500 hover:bg-red-600 text-white'
      : 'bg-cyan-500 hover:bg-cyan-600 text-white',
    btnSecondary: isDark
      ? 'bg-white/10 hover:bg-white/20 text-white/80'
      : 'bg-gray-100 hover:bg-gray-200 text-gray-700',
  };

  return (
    <div className={`fixed inset-0 ${theme.overlay} backdrop-blur-sm flex items-center justify-center p-6 z-[60]`}>
      <div className={`${theme.bg} rounded-2xl border ${theme.border} shadow-2xl max-w-md w-full p-6`}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
            confirmVariant === 'danger' ? 'bg-red-500/10' : 'bg-cyan-500/10'
          }`}>
            <AlertTriangle className={`w-8 h-8 ${
              confirmVariant === 'danger' ? 'text-red-400' : 'text-cyan-400'
            }`} />
          </div>
        </div>

        {/* Title */}
        {title && (
          <h3 className={`text-lg font-semibold text-center mb-2 ${theme.text}`}>
            {title}
          </h3>
        )}

        {/* Message */}
        <p className={`text-center mb-6 ${theme.textMuted}`}>
          {message}
        </p>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${theme.btnSecondary}`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${theme.btnPrimary}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

